<template functional>
  <div
    class="flex transition-opacity duration-300 ease-out relative"
    v-bind="data.attrs"
    :class="$options.methods.mergeFunctionalRenderContextClasses(data, {
      'items-center': props.labelPosition === 'right',
      'flex-col-reverse': props.labelPosition === 'top',
      'flex-row-reverse items-center': props.labelPosition === 'left',
      'opacity-50': props.disabled,
      'has-errors': props.errors.length > 0
    })"
  >
    <div
      :style="`height: ${height}px; ${props.required ? 'border-radius: 9999px; border-left: 3px solid #f5821f; ' : ''} ${props.inputStyle || ''}`"
    >
      <mia-toggle
        :id="`${$options.methods.kebabCase(props.label)}__input`"
        :value="props.value"
        :labels="props.labels"
        :font-size="props.fontSize"
        class="flex-none"
        :class="{ 'pointer-events-none': props.disabled || props.readonly }"
        :is-neutral="props.isNeutral"
        :width="props.width"
        :height="props.height"
        :required="props.required"
        v-on="{
          ...$options.methods.getFilteredListeners(listeners),
          input: (value) => {
            if (listeners.modelInput) {
              listeners.modelInput(value)
            }

            if (listeners.input) {
              listeners.input(value)
            }
          }
        }"
      />
    </div>
    <slot
      name="label"
      :forInput="`${$options.methods.kebabCase(props.label)}__input`"
    >
      <label
        v-if="props.label"
        :for="`${$options.methods.kebabCase(props.label)}__input`"
        class="leading-none tracking-wider text-gray-800"
        :class="{
          'mb-0 ml-2': props.labelPosition === 'right',
          'mb-2': props.labelPosition === 'top',
          'mb-0 mr-2': props.labelPosition === 'left',
          'pointer-events-none': props.disabled || props.readonly,
        }"
        @click="$options.methods.emitInput(listeners, !props.value)"
        v-text="props.label"
      />
    </slot>

    <mia-fade-transition>
      <!-- eslint-disable vue/no-v-html -->
      <small
        v-if="props.errors.length > 0"
        class="absolute top-0 block leading-3 text-red-600 error-message"
        v-html="props.errors[0]"
      />
      <!-- eslint-enable vue/no-v-html -->
    </mia-fade-transition>
  </div>
</template>

<script>
import MiaToggle from '@/components/ui/inputs/Toggle.vue'
import { mergeFunctionalRenderContextClasses } from '@/helpers/utils'
import { kebabCase } from 'lodash/string'

export default {
  name: 'MiaFeatureToggle',

  components: {
    'mia-toggle': MiaToggle
  },

  model: {
    // Having a v-model binding, but also providing an @input event listener
    // to do additional things when the input changes results in the following binding for "input":
    // input: [null, null]. This seems to be a restriction when using functional components. Therefore
    // we have to account for that and use an alias instead.
    event: 'modelInput'
  },

  props: {
    value: {
      type: Boolean,
      required: true
    },

    label: {
      type: String,
      default: ''
    },

    labelPosition: {
      type: String,
      default: 'right',
      validator(position) {
        const isValid = ['right', 'top', 'left'].includes(position)

        if (!isValid) {
          console.error('[mia-feature-toggle]: "labelPosition" must be one of "right", "left", or "top".')
        }

        return isValid
      }
    },

    labels: {
      type: [Boolean, Object],
      default() {
        return {
          checked: 'Ja',
          unchecked: 'Nein'
        }
      }
    },

    fontSize: {
      type: Number,
      default: 14
    },

    isNeutral: {
      type: Boolean,
      default: false
    },

    width: {
      type: Number,
      default: 80
    },

    height: {
      type: Number,
      default: 39
    },

    disabled: {
      type: Boolean,
      default: false
    },

    readonly: {
      type: Boolean,
      default: false
    },

    errors: {
      type: Array,
      default() {
        return []
      }
    },

    inputStyle: {
      type: String,
      default: ''
    },

    required: {
      type: Boolean,
      default: false
    }
  },

  methods: {
    mergeFunctionalRenderContextClasses,

    kebabCase,

    /**
     * Exclude the "modelInput" listener, if present, since that is a made up event listener name
     * which functions as alias for the "input" event, in order to workaround the issue described above.
     */
    getFilteredListeners(listeners) {
      const filteredListeners = {}

      Object.entries(listeners).forEach(([key, listener]) => {
        if (key !== 'modelInput') {
          filteredListeners[key] = listener
        }
      })

      return filteredListeners
    },

    emitInput(listeners, value) {
      if (listeners.modelInput) {
        listeners.modelInput(value)
      }

      if (listeners.input) {
        listeners.input(value)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.vue-js-switch {
  margin-bottom: 0 !important;
}
label {
  hyphens: auto;
}

.has-errors {

}

.has-errors ::v-deep .v-switch-core {
  @apply shadow-outline-error;
}

.has-errors .error-message {
  top: calc(100% + 6px);
}
</style>
